// src/components/clients/invoiceTypes.ts

// ============================================================
// Constants
// ============================================================

export const INVOICE_CONSTANTS = {
  companyDisplayName: "Real McCoy Realty Consultants",
  paymentAddressLines: ["713 Purslane Way", "Woodstock, GA 30188"],
  termsLines: [
    "Net 30",
    "Please make checks payable to Real McCoy Realty Consultants",
  ],
  footerLeft: "Thank you for your business.",
  footerRight: "Questions? Reply to this invoice email.",
  currency: "USD",
  defaultCommissionPercentage: 50,
  netDays: 30,
};

// ============================================================
// Invoice Status
// ============================================================

export type InvoiceStatus =
  | "draft"
  | "sent"
  | "paid"
  | "overdue"
  | "cancelled"
  | "failed";

// ============================================================
// PKT-0916 · what PATCH /invoices/{id}/status did to the client's stage
// ============================================================

export interface ClientStageSync {
  /** True when marking the invoice paid moved the client to payment_received. */
  moved: boolean;
  from_status?: string | null;
  to_status?: string;
  reason?: string;
  /** Set after an un-pay: the stage was left alone and this note was written. */
  note?: string;
  /** Provider F3: the stage would have moved, but the caller may not edit this client. */
  skipped_reason?: string;
}

/** An invoice that is still owed (sent / overdue) — what the reverse prompt offers. */
export interface OpenInvoiceSummary {
  id: string;
  invoice_number: string;
  status: string;
  amount: number;
  property_name?: string | null;
  due_date?: string | null;
}

// ============================================================
// Invoice Entity
// ============================================================

export interface Invoice {
  id: string;
  invoice_number: string;
  client_id: string;
  property_id: string;
  assigned_agent_id: string;

  // Financial
  base_rent: number;
  commission_percentage: number;
  amount: number;

  // Recipient
  recipient_name: string;
  recipient_email: string;
  bill_to_name: string;

  // Property
  property_name: string;
  property_address: string;

  // Client
  client_name: string;
  move_in_date: string;

  // Dates
  invoice_date: string;
  due_date: string;

  // Status
  status: InvoiceStatus;
  sent_at?: string;
  paid_at?: string;
  tracking_id?: string;

  // Storage
  pdf_storage_key?: string;
  pdf_base64?: string;

  // Audit
  created_at: string;
  created_by: string;
  updated_at?: string;
}

// ============================================================
// Property & Contact Types
// ============================================================

export interface PropertyContact {
  id: string;
  name: string;
  email: string;
  role?: string;
}

export interface InvoiceProperty {
  id: string;
  name: string;
  address: string;
  property_management?: string;
  contacts: PropertyContact[];
}

// ============================================================
// Form Types
// ============================================================

export interface InvoiceFormState {
  propertyId: string;
  recipientContactId: string;
  recipientName: string;
  recipientEmail: string;
  billToName: string;
  propertyName: string;
  propertyAddress: string;
  baseRent: string;
  commissionPercentage: string;
  commissionBasis?: "percentage" | "flat";
  amount: string;
  invoiceDate: string;
  dueDate: string;
  moveInDate: string;
}

export interface InvoiceFormErrors {
  general?: string;
  propertyId?: string;
  recipientEmail?: string;
  baseRent?: string;
  commissionPercentage?: string;
  amount?: string;
  moveInDate?: string;
}

// ============================================================
// API Request/Response Types
// ============================================================

export interface CreateInvoiceRequest {
  clientId: string;
  propertyId: string;
  baseRent: number;
  commissionPercentage: number;
  commissionBasis?: "percentage" | "flat";
  amount: number;
  recipientName: string;
  recipientEmail: string;
  recipientEmails?: string[]; // Support for multiple recipients (CC)
  billToName: string;
  propertyName: string;
  propertyAddress: string;
  clientName: string;
  moveInDate: string;
  invoiceDate: string;
  dueDate: string;
  agentName: string;
  // PKT-0483: client-minted durable send action key (UUID). The same key is
  // retained across double-click, timeout, retry, and reload so the backend
  // can return existing delivery truth instead of sending a second invoice.
  sendActionId?: string;
}

// PKT-0483 delivery truth, separate from financial invoice status.
export type InvoiceDeliveryState =
  | "preparing"
  | "dispatch_inflight"
  | "accepted"
  | "definite_failure"
  | "delivery_unknown"
  | "accepted_reconciliation_required"
  | "legacy_delivery_unknown";

export interface CreateInvoiceResponse {
  success: boolean;
  invoice?: Invoice;
  pdfUrl?: string;
  error?: string;
  // PKT-0496 operator truth: the modal builds success/warning text from
  // these SUBMITTED counts, never from its original requested array.
  requestedRecipientCount?: number;
  submittedRecipientCount?: number;
  blockedCcCount?: number;
  // PKT-0483: on delivery_unknown / accepted_reconciliation_required the
  // modal must hide ordinary Send — a resend can duplicate a real invoice.
  deliveryState?: InvoiceDeliveryState;
  sendActionId?: string;
}

export interface PreviewInvoiceResponse {
  success: boolean;
  pdfBase64?: string;
  invoiceNumber?: string;
  s3Key?: string;
  error?: string;
}

// ============================================================
// PDF Payload Types
// ============================================================

export interface InvoiceLineItem {
  description: string;
  baseRent: number;
  commissionText: string;
  amount: number;
}

export interface InvoicePdfPayload {
  companyDisplayName: string;
  invoiceNumber: string;
  currency: string;
  balanceDue: number;
  billToName: string;
  paymentAddressLines: string[];
  property: {
    name: string;
    address: string;
  };
  invoiceDate: string;
  clientName: string;
  moveInDate: string;
  agentName: string;
  items: InvoiceLineItem[];
  total: number;
  termsLines: string[];
  footerLeft: string;
  footerRight: string;
}
